import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import taskApi from '../services/taskApi';
import useLanguage from '../hooks/useLanguage';
import { formatShortDateTime } from '../utils/dateFormatter';
import { toRiyadhInputValue, riyadhInputValueToIso } from '../utils/dateValueHelper';
import { taskRefusalKey } from '../utils/taskErrorCodes';
import { FIELD_CREW_ROLES } from '../config/roleConfig';

// Mirrors CORRECTION_REASON_MAX in be/config/tasks.js (= a chase note's cap).
const REASON_MAX = 500;

/**
 * «سجل النظافة» — a supervisor reads and corrects a clean's record: the start time and who took part
 * (tasks/cleaning-performance-report.md D7). The two facts the per-cleaner report is built on.
 *
 * Its own section with its own save, NOT part of the edit form: a correction needs a reason and files
 * a history row, and must not ride along silently with a title or priority edit. The server keeps the
 * original; this panel shows every correction filed, newest first, so a figure can always be traced.
 *
 * Rendered by the parent only for task managers, on a clean that is under way or finished.
 * `staffUsers` is the dialog's own staff list — narrowed here to the field crew.
 */
const CleanRecordCorrection = ({ task, staffUsers = [], onCorrected }) => {
  // `onCorrected(record)` lets the dialog refresh what it shows from the same record (the
  // «استغرقت» line) without closing — the dialog's own `onTaskUpdated` closes it on some pages.
  const { t, isArabic, language } = useLanguage();
  const [record, setRecord] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [startValue, setStartValue] = useState('');
  const [team, setTeam] = useState([]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoadError(false);
      const response = await taskApi.getCleanRecord(task._id);
      setRecord(response.data);
    } catch (err) {
      setLoadError(true);
    }
  }, [task._id]);

  useEffect(() => { load(); }, [load]);

  // The field crew to tick from — plus anyone already on the record who is not in that list (a role
  // changed since), so a wrong name can always be unticked.
  const crew = [
    ...staffUsers.filter(u => FIELD_CREW_ROLES.includes(u.role)),
    ...(record?.cleaners || []).filter(c => !staffUsers.some(u => u._id === c._id && FIELD_CREW_ROLES.includes(u.role)))
  ];
  const nameOf = (id) => crew.find(u => u._id === id)?.name
    || record?.cleaners.find(c => c._id === id)?.name
    || '—';

  const openEditor = () => {
    setStartValue(toRiyadhInputValue(record.startedAt));
    setTeam(record.cleaners.map(c => c._id));
    setReason('');
    setError(null);
    setSaved(false);
    setEditing(true);
  };

  const toggleCleaner = (id) => {
    setTeam(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const handleSave = async () => {
    if (!reason.trim()) {
      setError(t('tasks.cleanRecord.invalidReason'));
      return;
    }
    // Send only what actually changed — the server files one history row per corrected fact.
    const change = { reason: reason.trim() };
    const startIso = riyadhInputValueToIso(startValue);
    const currentStart = record.startedAt ? new Date(record.startedAt).toISOString() : null;
    if (startIso && startIso !== currentStart) change.startedAt = startIso;
    // Compared as a set, as the server does — the same people in another order is no change.
    const currentTeam = record.cleaners.map(c => c._id);
    const sameTeam = team.length === currentTeam.length && team.every(id => currentTeam.includes(id));
    if (!sameTeam) change.cleanerUserIds = team;
    if (!change.startedAt && !change.cleanerUserIds) {
      setError(t('tasks.cleanRecord.invalidChange'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const response = await taskApi.correctCleanRecord(task._id, change);
      setRecord(response.data);
      setEditing(false);
      setSaved(true);
      if (onCorrected) onCorrected(response.data);
    } catch (err) {
      const key = taskRefusalKey(err?.response?.data);
      setError(key ? t(key) : t('tasks.cleanRecord.failed'));
    } finally {
      setSaving(false);
    }
  };

  const describeChange = (details) => {
    if (details?.field === 'startedAt') {
      const from = details.from ? formatShortDateTime(details.from, language) : t('tasks.cleanRecord.noStart');
      return `${t('tasks.cleanRecord.changedStart')}: ${from} → ${formatShortDateTime(details.to, language)}`;
    }
    const names = (details?.to || []).map(nameOf);
    return `${t('tasks.cleanRecord.changedTeam')}: ${names.length ? names.join(' · ') : t('tasks.cleanRecord.noTeam')}`;
  };

  return (
    <div
      className="p-3 rounded mb-3"
      style={{ background: '#f4f0fb', borderInlineStart: '3px solid #471396' }}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <div className="d-flex align-items-center justify-content-between mb-2">
        <Form.Label className="fw-semibold mb-0" style={{ color: '#471396' }}>
          {t('tasks.cleanRecord.title')}
        </Form.Label>
        {record && !editing && (
          <Button variant="outline-primary" size="sm" onClick={openEditor}
            style={{ color: '#471396', borderColor: '#471396' }}>
            {t('tasks.cleanRecord.correctButton')}
          </Button>
        )}
      </div>

      {loadError && <div className="small text-danger">{t('tasks.cleanRecord.loadFailed')}</div>}
      {!record && !loadError && <Spinner animation="border" size="sm" />}

      {record && !editing && (
        <>
          <div className="small mb-1">
            ⏱ {t('tasks.cleanRecord.startLabel')}:{' '}
            {record.startedAt ? formatShortDateTime(record.startedAt, language) : t('tasks.cleanRecord.noStart')}
          </div>
          <div className="small mb-2">
            👥 {t('tasks.cleanRecord.teamLabel')}:{' '}
            {record.cleaners.length
              ? record.cleaners.map(c => c.name).join(' · ')
              : t('tasks.cleanRecord.noTeam')}
          </div>
          {saved && <div className="small text-success mb-2">✓ {t('tasks.cleanRecord.saved')}</div>}
          {record.corrections.map(c => (
            // A system row is the one-off backfill (v1.2.30): nobody corrected anything — the credit came
            // from the start tap. Said in the reader's language, never the stored English note, no "by —".
            c.actorType === 'system' ? (
              <div key={c._id} className="small text-muted mb-1">ⓘ {t('tasks.cleanRecord.autoFilled')}</div>
            ) : (
              <div key={c._id} className="small text-muted mb-1">
                ✎ {t('tasks.cleanRecord.correctedBy')} {c.actorName || '—'} · {formatShortDateTime(c.at, language)}
                <div>{describeChange(c.details)}</div>
                {c.details?.reason && <div>{c.details.reason}</div>}
              </div>
            )
          ))}
        </>
      )}

      {record && editing && (
        <>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">{t('tasks.cleanRecord.startInput')}</Form.Label>
            <Form.Control
              type="datetime-local"
              size="sm"
              value={startValue}
              onChange={(e) => setStartValue(e.target.value)}
              dir="ltr"
            />
            <Form.Text className="text-muted">{t('tasks.cleanRecord.startHint')}</Form.Text>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">{t('tasks.cleanRecord.teamInput')}</Form.Label>
            {crew.map(u => (
              <Form.Check
                key={u._id}
                type="checkbox"
                id={`clean-record-${task._id}-${u._id}`}
                label={u.name}
                checked={team.includes(u._id)}
                onChange={() => toggleCleaner(u._id)}
              />
            ))}
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">{t('tasks.cleanRecord.reasonInput')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              size="sm"
              value={reason}
              maxLength={REASON_MAX}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('tasks.cleanRecord.reasonPlaceholder')}
            />
          </Form.Group>

          {error && <div className="small text-danger mb-2" role="alert">{error}</div>}

          <div className="d-flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving}
              style={{ background: '#471396', borderColor: '#471396' }}>
              {saving ? <Spinner animation="border" size="sm" /> : t('tasks.cleanRecord.save')}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(false)} disabled={saving}>
              {t('tasks.cleanRecord.cancel')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default CleanRecordCorrection;
